import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const inputCls = "w-full bg-[#0F0F0F] border border-white/10 text-white text-sm px-4 py-3 rounded-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-200";

const homeFor = (role) => role === "admin" ? "/admin" : role === "coach" ? "/coach" : "/portal";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // If AuthContext is still restoring the session, wait for it — never blank the screen.
  if (user === null) {
    return (
      <main className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="login-hydrating">
        <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase font-semibold animate-pulse">Loading…</p>
      </main>
    );
  }
  // Already authenticated — redirect declaratively (never call navigate() during render).
  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    if (!res.ok) {
      setLoading(false);
      setError(res.error);
      return;
    }
    // Defer the router transition to the next microtask so React has committed
    // the AuthContext state update first — this avoids the "blank until refresh"
    // race that hit Safari and installed PWAs.
    setTimeout(() => navigate(homeFor(res.user.role), { replace: true }), 0);
  };

  return (
    <main className="min-h-screen bg-[#050505] flex items-center justify-center px-6 noise-overlay relative" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-1 justify-center mb-10" data-testid="login-logo">
          <img src="/logo.png" alt="Zavi Football Club" className="h-16 w-auto" />
          <span className="font-heading text-3xl font-black tracking-tighter text-white leading-none">
            ZAVI<span className="text-[#D4AF37]">FC</span>
          </span>
        </Link>
        <form onSubmit={submit} className="border border-white/10 bg-[#0A0A0A] p-10 space-y-5" data-testid="login-form">
          <div className="mb-2">
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Member Login</p>
            <h1 className="font-heading text-2xl font-black uppercase tracking-tight mt-2">Welcome Back</h1>
          </div>
          {error && <p className="text-red-400 text-sm border border-red-400/30 bg-red-400/10 px-4 py-3" data-testid="login-error">{error}</p>}
          <input type="email" required placeholder="Email" value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} className={inputCls} data-testid="login-email" />
          <div className="relative">
            <input type={showPassword ? "text" : "password"} required placeholder="Password" value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} className={inputCls + " pr-12"} data-testid="login-password" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#D4AF37] transition-colors duration-150" data-testid="login-password-toggle">
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-sm px-10 py-4 rounded-sm transition-all duration-200 hover:bg-[#F3D059] gold-glow disabled:opacity-50">
            {loading ? "Signing in..." : "Sign In"}
          </button>
          <p className="text-white/40 text-xs text-center pt-2">
            The accounts are created by Zavi Football Club. Kindly contact the club on{" "}
            <a href="https://wa.me/919873628804" target="_blank" rel="noreferrer" className="text-[#D4AF37] hover:text-[#F3D059] underline underline-offset-2" data-testid="login-whatsapp-link">WhatsApp</a>{" "}
            if you need any help.
          </p>
        </form>
      </div>
    </main>
  );
}
